/**
 * Yanaa homepage / landing video carousel.
 * One shared custom element for blocks/custom-video-block-v2.liquid.
 */
(() => {
  if (customElements.get('yanaa-video-gallery')) return;

  const POSITIONS = ['is-far-left', 'is-left', 'is-center', 'is-right', 'is-far-right'];

  class YanaaVideoGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.stage = this.querySelector('.yvg__stage');
      this.slides = [...this.querySelectorAll('.yvg__slide')];
      this.prevBtn = this.querySelector('.yvg__nav--prev');
      this.nextBtn = this.querySelector('.yvg__nav--next');
      this.total = this.slides.length;
      this.index = 0;
      this.animating = false;
      this.inView = false;
      this.touchX = null;

      if (!this.total) return;

      this.prevBtn?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.navigate(-1);
      });
      this.nextBtn?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.navigate(1);
      });

      this.slides.forEach((slide, i) => {
        slide.addEventListener('click', () => {
          if (i !== this.index) this.goTo(i);
        });

        const video = slide.querySelector('video');
        const bar = slide.querySelector('.yvg__progress-bar');
        const mute = slide.querySelector('.yvg__mute');
        if (!video) return;

        video.addEventListener('timeupdate', () => {
          if (!video.duration || !bar) return;
          bar.style.width = `${(video.currentTime / video.duration) * 100}%`;
        });

        mute?.addEventListener('click', (event) => {
          event.stopPropagation();
          video.muted = !video.muted;
          mute.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
        });
      });

      this.stage?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigate(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigate(1);
        }
      });

      this.stage?.addEventListener('touchstart', (event) => {
        this.touchX = event.changedTouches?.[0]?.clientX ?? null;
      }, { passive: true });

      this.stage?.addEventListener('touchend', (event) => {
        if (this.touchX == null) return;
        const x = event.changedTouches?.[0]?.clientX;
        const delta = (x ?? this.touchX) - this.touchX;
        this.touchX = null;
        if (Math.abs(delta) < 40) return;
        this.navigate(delta > 0 ? -1 : 1);
      }, { passive: true });

      this.viewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const entered = entry.isIntersecting && !this.inView;
          this.inView = entry.isIntersecting;
          if (entered) this.render();
          const centerVideo = this.slides[this.index]?.querySelector('video');
          if (!centerVideo) return;
          if (this.inView) centerVideo.play().catch(() => {});
          else centerVideo.pause();
        });
      }, { threshold: 0.25 });
      this.viewObserver.observe(this);

      // A24 (QA gap G6): the centre slide's poster and video stream are attached only once the gallery
      // is within 200 px of the viewport. It sits ~4,800 px down the home page, and arming it at load
      // cost 5 MB (Chrome) / 25-28 MB (WebKit, HLS ignores preload="none") before any scroll.
      this.nearView = false;
      this.nearObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const entered = entry.isIntersecting && !this.nearView;
          this.nearView = entry.isIntersecting;
          if (entered) this.render();
        });
      }, { rootMargin: '200px 0px' });
      this.nearObserver.observe(this);

      this.render();
    }

    disconnectedCallback() {
      this.viewObserver?.disconnect();
      this.nearObserver?.disconnect();
    }

    navigate(direction) {
      if (this.animating || !this.total) return;
      this.animating = true;
      this.index = (this.index + direction + this.total) % this.total;
      this.render();
      window.setTimeout(() => { this.animating = false; }, this.motionMs());
    }

    goTo(target) {
      if (this.animating || target === this.index) return;
      this.animating = true;
      this.index = target;
      this.render();
      window.setTimeout(() => { this.animating = false; }, this.motionMs());
    }

    motionMs() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 1;
      return 800;
    }

    wrap(offset) {
      return (this.index + offset + this.total) % this.total;
    }

    armVideo(video) {
      if (!video || video.dataset.armed === 'true') return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
      });
      if (video.dataset.poster) video.poster = video.dataset.poster;
      video.load();
      video.dataset.armed = 'true';
    }

    render() {
      const map = {
        [this.wrap(0)]: 'is-center',
        [this.wrap(-1)]: 'is-left',
        [this.wrap(1)]: 'is-right',
        [this.wrap(-2)]: 'is-far-left',
        [this.wrap(2)]: 'is-far-right',
      };

      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        POSITIONS.forEach((cls) => slide.classList.remove(cls));
        const pos = map[i];
        if (pos) slide.classList.add(pos);

        if (pos === 'is-center') {
          if (video && this.inView) {
            this.armVideo(video);
            video.play().catch(() => {});
          } else if (video && this.nearView) {
            this.armVideo(video);
          }
        } else if (video) {
          video.pause();
        }
      });

      this.prevBtn?.setAttribute('aria-disabled', this.total < 2 ? 'true' : 'false');
      this.nextBtn?.setAttribute('aria-disabled', this.total < 2 ? 'true' : 'false');
    }
  }

  customElements.define('yanaa-video-gallery', YanaaVideoGallery);
})();
